import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { Badge } from '@mantine/core'
import { useMedplum } from '@medplum/react'
import type { MedicationRequest } from '@medplum/fhirtypes'
import { formatDateTime, formatPatientName } from '@/utils'
import { logError } from '@/utils/errorUtils'
import {
  doseSpotDispenseLabel,
  doseSpotDrugCodeLabel,
  doseSpotMedicationText,
  doseSpotPrescriberLabel,
} from '../doseSpotPrescription'
import { DoseSpotStatusBadge } from '../DoseSpotStatusBadge'

type Props = {
  prescription: MedicationRequest
}

const availFields = [
  { code: 'medication', title: 'Medication' },
  { code: 'code', title: 'Drug code' },
  { code: 'instructions', title: 'Instructions' },
  { code: 'dispense', title: 'Dispense' },
  { code: 'status', title: 'Status' },
  { code: 'written', title: 'Written' },
  { code: 'prescriber', title: 'Prescriber' },
  { code: 'source', title: 'Source' },
]

const renderColumn = (prescription: MedicationRequest, itemCode: string): string | JSX.Element => {
  switch (itemCode) {
    case 'medication':
      return doseSpotMedicationText(prescription) || '-'
    case 'code':
      // Absent on a FreeText or Compound prescription, which is text-only by construction.
      return doseSpotDrugCodeLabel(prescription) || 'Uncoded'
    case 'instructions':
      // One free-text sig, exactly as the prescriber wrote it — no structured parsing in v1.
      return prescription.dosageInstruction?.[0]?.text || ''
    case 'dispense':
      return doseSpotDispenseLabel(prescription)
    case 'status':
      return <DoseSpotStatusBadge prescription={prescription} />
    case 'written':
      return prescription.authoredOn ? formatDateTime(prescription.authoredOn) : ''
    case 'prescriber':
      return doseSpotPrescriberLabel(prescription)
    case 'source':
      return (
        <Badge size="xs" variant="light" color="blue">
          DoseSpot
        </Badge>
      )
    default:
      return ''
  }
}

/**
 * The chronological record's detail panel for a DoseSpot-synced prescription — the
 * `MedicationRequest` counterpart of this folder's `Medications` (which reads
 * `MedicationStatement`). They stay separate components because the two resources share
 * no fields worth unifying: DoseSpot owns prescriptions, the note owns reported
 * medications, and neither is ever rendered as the other (§11.7).
 *
 * Read-only throughout: every sync replaces the row, so nothing here is BeHuman's to edit.
 */
export function DoseSpotPrescription({ prescription }: Props) {
  const medplum = useMedplum()
  // The sync writes `requester` as a bare reference; the name is the chart's to resolve.
  const [prescriberName, setPrescriberName] = useState<string>()

  const requesterReference = prescription.requester?.reference

  useEffect(() => {
    if (!requesterReference?.startsWith('Practitioner/')) return
    let active = true
    medplum
      .readReference({ reference: requesterReference })
      .then((resource) => {
        if (!active || resource.resourceType !== 'Practitioner') return
        setPrescriberName(formatPatientName(resource.name?.[0]) || undefined)
      })
      .catch((error: unknown) => {
        // A name that will not resolve leaves the row reading as it would for any
        // unnamed requester; it never blocks the rest of the prescription.
        logError('dosespot', 'failed to resolve the prescription requester', error)
      })
    return () => {
      active = false
    }
  }, [medplum, requesterReference])

  const displayed: MedicationRequest = prescriberName
    ? { ...prescription, requester: { ...prescription.requester, display: prescriberName } }
    : prescription

  return (
    <div className="border-b-1 border-b-gray-200 p-2">
      <table className="text-base">
        <tbody>
          {availFields.map((item) => (
            <tr key={item.code}>
              <td className="py-1 text-sm font-bold text-[#7D7987]">{item.title}:</td>
              <td className="px-[8px] py-1 text-sm">
                <div>{renderColumn(displayed, item.code)}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
